import { ThemeOptions, Components, Theme } from '@mui/material';

/**
 * Base typography settings shared across all themes
 */
export const baseTypography: ThemeOptions['typography'] = {
  fontFamily: '"Inter", "Roboto", "Helvetica", "Arial", sans-serif',
  h1: { fontWeight: 700, fontSize: '2.5rem', lineHeight: 1.2, letterSpacing: '-0.02em' },
  h2: { fontWeight: 600, fontSize: '2rem', lineHeight: 1.3, letterSpacing: '-0.01em' },
  h3: { fontWeight: 600, fontSize: '1.5rem', lineHeight: 1.4 },
  h4: { fontWeight: 600, fontSize: '1.25rem', lineHeight: 1.4 },
  h5: { fontWeight: 600, fontSize: '1.125rem', lineHeight: 1.5 },
  h6: { fontWeight: 600, fontSize: '1rem', lineHeight: 1.5 },
  body1: { fontSize: '1rem', lineHeight: 1.6, letterSpacing: '0.00938em' },
  body2: { fontSize: '0.875rem', lineHeight: 1.6, letterSpacing: '0.01071em' },
  button: { fontWeight: 500, fontSize: '0.875rem', lineHeight: 1.75, letterSpacing: '0.02857em', textTransform: 'none' },
};

/**
 * Base shape settings
 */
export const baseShape: ThemeOptions['shape'] = {
  borderRadius: 8,
};

/**
 * Custom shadows (Tailwind-inspired)
 */
export const baseShadows: ThemeOptions['shadows'] = [
  'none',
  '0px 1px 2px rgba(0, 0, 0, 0.05)',
  '0px 1px 3px rgba(0, 0, 0, 0.1), 0px 1px 2px rgba(0, 0, 0, 0.06)',
  '0px 4px 6px -1px rgba(0, 0, 0, 0.1), 0px 2px 4px -1px rgba(0, 0, 0, 0.06)',
  '0px 10px 15px -3px rgba(0, 0, 0, 0.1), 0px 4px 6px -2px rgba(0, 0, 0, 0.05)',
  '0px 20px 25px -5px rgba(0, 0, 0, 0.1), 0px 10px 10px -5px rgba(0, 0, 0, 0.04)',
  '0px 25px 50px -12px rgba(0, 0, 0, 0.25)',
  '0px 25px 50px -12px rgba(0, 0, 0, 0.25)',
  '0px 25px 50px -12px rgba(0, 0, 0, 0.25)',
  '0px 25px 50px -12px rgba(0, 0, 0, 0.25)',
  '0px 25px 50px -12px rgba(0, 0, 0, 0.25)',
  '0px 25px 50px -12px rgba(0, 0, 0, 0.25)',
  '0px 25px 50px -12px rgba(0, 0, 0, 0.25)',
  '0px 25px 50px -12px rgba(0, 0, 0, 0.25)',
  '0px 25px 50px -12px rgba(0, 0, 0, 0.25)',
  '0px 25px 50px -12px rgba(0, 0, 0, 0.25)',
  '0px 25px 50px -12px rgba(0, 0, 0, 0.25)',
  '0px 25px 50px -12px rgba(0, 0, 0, 0.25)',
  '0px 25px 50px -12px rgba(0, 0, 0, 0.25)',
  '0px 25px 50px -12px rgba(0, 0, 0, 0.25)',
  '0px 25px 50px -12px rgba(0, 0, 0, 0.25)',
  '0px 25px 50px -12px rgba(0, 0, 0, 0.25)',
  '0px 25px 50px -12px rgba(0, 0, 0, 0.25)',
  '0px 25px 50px -12px rgba(0, 0, 0, 0.25)',
  '0px 25px 50px -12px rgba(0, 0, 0, 0.25)',
];

/**
 * Light mode component overrides
 */
export const lightModeComponents: Components<Theme> = {
  MuiPaper: {
    styleOverrides: {
      root: {
        boxShadow: '0px 1px 3px rgba(0, 0, 0, 0.1), 0px 1px 2px rgba(0, 0, 0, 0.06)',
        backgroundImage: 'none',
      },
      elevation1: { boxShadow: '0px 1px 2px rgba(0, 0, 0, 0.05)' },
      elevation2: { boxShadow: '0px 1px 3px rgba(0, 0, 0, 0.1), 0px 1px 2px rgba(0, 0, 0, 0.06)' },
      elevation3: { boxShadow: '0px 4px 6px -1px rgba(0, 0, 0, 0.1), 0px 2px 4px -1px rgba(0, 0, 0, 0.06)' },
    },
  },
  MuiButton: {
    styleOverrides: {
      root: {
        textTransform: 'none',
        borderRadius: 8,
        fontWeight: 500,
        padding: '8px 16px',
        transition: 'all 0.2s ease-in-out',
        '&:hover': {
          transform: 'translateY(-1px)',
          boxShadow: '0px 4px 6px -1px rgba(0, 0, 0, 0.1), 0px 2px 4px -1px rgba(0, 0, 0, 0.06)',
        },
      },
      contained: {
        boxShadow: '0px 1px 2px rgba(0, 0, 0, 0.05)',
        '&:hover': {
          boxShadow: '0px 4px 6px -1px rgba(0, 0, 0, 0.1), 0px 2px 4px -1px rgba(0, 0, 0, 0.06)',
        },
      },
      outlined: {
        borderWidth: 1.5,
        '&:hover': {
          borderWidth: 1.5,
          backgroundColor: 'rgba(15, 100, 102, 0.04)',
        },
      },
    },
  },
  MuiTextField: {
    styleOverrides: {
      root: {
        '& .MuiOutlinedInput-root': {
          borderRadius: 8,
          transition: 'all 0.2s ease-in-out',
          '&:hover': {
            '& .MuiOutlinedInput-notchedOutline': {
              borderColor: '#94A3B8',
            },
          },
          '&.Mui-focused': {
            '& .MuiOutlinedInput-notchedOutline': {
              borderWidth: 2,
            },
          },
        },
      },
    },
  },
  MuiCard: {
    styleOverrides: {
      root: {
        borderRadius: 12,
        boxShadow: '0px 1px 3px rgba(0, 0, 0, 0.1), 0px 1px 2px rgba(0, 0, 0, 0.06)',
        transition: 'all 0.2s ease-in-out',
        '&:hover': {
          boxShadow: '0px 4px 6px -1px rgba(0, 0, 0, 0.1), 0px 2px 4px -1px rgba(0, 0, 0, 0.06)',
        },
      },
    },
  },
  MuiChip: {
    styleOverrides: {
      root: {
        borderRadius: 6,
        fontWeight: 500,
      },
    },
  },
  MuiTableCell: {
    styleOverrides: {
      root: { borderColor: '#E2E8F0' },
      head: { fontWeight: 600, backgroundColor: '#F8FAFC', color: '#0F172A' },
    },
  },
  MuiAppBar: {
    styleOverrides: {
      root: {
        boxShadow: '0px 1px 3px rgba(0, 0, 0, 0.1), 0px 1px 2px rgba(0, 0, 0, 0.06)',
      },
    },
  },
  MuiAlert: {
    styleOverrides: {
      filledWarning: {
        color: '#FFFFFF',
        '& .MuiAlert-icon': {
          color: '#FFFFFF',
        },
      },
      filledError: {
        color: '#FFFFFF',
        '& .MuiAlert-icon': {
          color: '#FFFFFF',
        },
      },
    },
  },
};
